import React, { useState, useRef, useLayoutEffect } from 'react';
import { LuArrowRight, LuStethoscope, LuLeaf, LuStar } from 'react-icons/lu';
import { ROUTINE_ROUTES } from '../../contracts/oracleApi';
import { ADHERENCE_META } from '../../contracts/routineApi';
import { track } from '../../analytics/track';
import AurumButton from '../oracle/AurumButton';
import VectorPill from '../oracle/VectorPill';

// Fade the last line when the title is clamped (mobile 3-line overflow).
const TITLE_FADE = 'linear-gradient(to bottom, #000 calc(100% - 1.2em), transparent 100%)';

// True when `el` is clamped (content taller than the box). Re-measures on
// resize so it tracks the responsive max-height (clamp is mobile-only).
function useClamped(dep) {
  const ref = useRef(null);
  const [clamped, setClamped] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setClamped(el.scrollHeight - el.clientHeight > 1);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    return undefined;
  }, [dep]);
  return [ref, clamped];
}

// RoutineCard — one product on the Routine page. Layout:
//   [thumbnail]  Title (underlined affiliate link) · step/use-time
//                ★ rating · N reviews
//                active — for {treats}
//                [tag] [tag] [tag]
//   (strength rung + pip ladder, when the active has multiple tiers)
//   [ SHOP NOW · $price ]   ← gold AurumButton, full width, bottom
//   ──────────── divider ────────────
//   USING THIS?  [YES][SOMETIMES][NO]   ← adherence, below the shop button
//
// Non-product routes (clinician / behavioral) render as an advisory card: the
// AI coaching note, no thumbnail, no price, no affiliate, no adherence.
//
// `active` is a RoutineActive (see src/contracts/oracleApi.js typedef).

const PIP_STYLE = {
  climbed: { background: 'rgba(201,164,92,0.5)', boxShadow: 'none' },
  current: { background: '#E6C083', boxShadow: '0 0 8px rgba(201,164,92,0.65)' },
  reachable: { background: 'rgba(255,255,255,0.13)', boxShadow: 'none' },
  locked: { background: 'rgba(201,90,70,0.4)', boxShadow: 'none' },
};

// Client-side echo of the affiliate click.
//
// The AUTHORITATIVE count is written server-side by the /go/ redirect this link
// points at (functions/affiliateFunctions/affiliateRedirect.js). This call is
// kept deliberately: comparing the client count with the server count is a free
// running estimate of our ad-block rate, which matters because affiliate CTR is
// a Phase 0 kill gate and it used to be measured ONLY here — silently returning
// nothing whenever googletagmanager.com was blocked.
//
// track() never throws, so this cannot block navigation.
function trackShop(active) {
  track('affiliate_click', {
    active_id: active.active_id,
    product_id: active.product_id,
    brand: active.brand,
    product: active.product,
  });
}

// Full-width product image on top of the card (standard shopping-card layout),
// with a graceful fallback to the active's initial when missing or it fails to load.
const ProductImage = ({ src, alt, initial }) => {
  const [errored, setErrored] = useState(false);
  const frame = 'w-full h-44 shrink-0 border-b border-hairline-soft';
  if (src && !errored) {
    return (
      <div className={`${frame} bg-[#0e0e0d]`}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onError={() => setErrored(true)}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  return (
    <div
      className={`${frame} flex items-center justify-center`}
      style={{ background: 'linear-gradient(180deg, #3b3b39, #232322)' }}
      aria-hidden="true"
    >
      <span className="font-censor text-aurum text-4xl font-bold">{initial}</span>
    </div>
  );
};

// Clinician / behavioral advisory card — no product, no thumbnail, no affiliate.
// Coaching copy stays in the friendly SummaryBlock register (per DESIGN.md).
const AdvisoryCard = ({ active }) => {
  const isClinician = active.route === ROUTINE_ROUTES.CLINICIAN;
  const tag = isClinician ? 'SEE A PROFESSIONAL' : 'BEHAVIORAL';
  const icon = isClinician
    ? <LuStethoscope size={15} strokeWidth={1.5} />
    : <LuLeaf size={15} strokeWidth={1.5} />;
  return (
    <div className="bg-[#161615] border border-hairline-soft rounded-card p-[18px] font-display">
      <div className="flex items-center gap-[13px]">
        <div
          className="w-10 h-10 rounded-[9px] border border-hairline-soft flex items-center justify-center shrink-0 text-bone-dim"
          style={{ background: 'linear-gradient(180deg, #3b3b39, #232322)' }}
          aria-hidden="true"
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[17px] text-bone">{active.active}</span>
            <span className="text-[10px] tracking-[0.18em] text-bone-dim shrink-0">{tag}</span>
          </div>
          {active.treats ? (
            <div className="text-[13.5px] italic text-bone-dim mt-px">For {active.treats}</div>
          ) : null}
        </div>
      </div>
      {active.coaching ? (
        <p className="text-[14px] leading-relaxed text-[#C9C3B7] mt-3.5 m-0">{active.coaching}</p>
      ) : null}
    </div>
  );
};

const ProductCard = ({ active, onAdherence, busy = false }) => {
  const [titleRef, titleClamped] = useClamped(active.product);
  const pips = Array.isArray(active.pips) ? active.pips : [];
  // Drop redundant timing tags (AM/PM/BOTH) — timing is shown by MORNING/EVENING.
  const tags = (Array.isArray(active.tags) ? active.tags : [])
    .filter((t) => !['AM', 'PM', 'BOTH'].includes(String(t).toUpperCase()));
  const hasLadder = (active.rung_count || 1) > 1;
  const rungCaption = `RUNG ${(active.rung_index || 0) + 1} / ${active.rung_count || 1}`;
  const href = active.affiliate_url || '#';
  const initial = active.active ? active.active.charAt(0) : '·';

  const openShop = () => {
    trackShop(active);
    if (active.affiliate_url && typeof window !== 'undefined') {
      window.open(active.affiliate_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="bg-[#161615] border border-hairline-soft rounded-card overflow-hidden font-display h-full flex flex-col">
      {/* product image on top (standard shopping-card layout) */}
      <ProductImage src={active.image_url} alt={active.product} initial={initial} />
      <div className="p-[18px] flex flex-col flex-1">
          {/* title = underlined affiliate hyperlink, clamped to 3 lines + fade */}
          <a
            ref={titleRef}
            href={href}
            target="_blank"
            // `sponsored` is required disclosure for a paid affiliate link and
            // was previously applied on some surfaces but not this one.
            rel="noopener noreferrer sponsored"
            onClick={() => trackShop(active)}
            className="block overflow-hidden max-h-[4.125em] text-[15px] text-bone leading-snug underline underline-offset-2 decoration-[rgba(220,215,206,0.45)] hover:text-aurum-bright hover:decoration-aurum-bright transition-colors duration-200"
            style={titleClamped ? { WebkitMaskImage: TITLE_FADE, maskImage: TITLE_FADE } : undefined}
          >
            {active.product}
          </a>

          {/* review, under the title */}
          {active.rating != null && (
            <div className="flex items-center gap-1.5 mt-1 text-[12px] text-bone-dim">
              <LuStar size={12} className="text-aurum" fill="currentColor" strokeWidth={0} aria-hidden="true" />
              <span className="text-bone">{active.rating}</span>
              {active.review_count != null && (
                <span>· {Number(active.review_count).toLocaleString()} reviews</span>
              )}
            </div>
          )}

          {/* what it treats */}
          <div className="text-[13px] italic text-bone-dim mt-1">
            {active.active}{active.treats ? ` — for ${active.treats}` : ''}
          </div>

          {/* item tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {tags.map((t) => (
                <VectorPill key={t}>{t}</VectorPill>
              ))}
            </div>
          )}

      {/* strength rung (only when the active has multiple tiers) */}
      {hasLadder && (
        <div className="mt-4">
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-[#E6C083]">{active.rung_name}</span>
            <span className="text-[10px] tracking-[0.16em] text-bone-dim">{rungCaption}</span>
          </div>
          <div className="flex gap-1 mt-2">
            {pips.map((p, i) => (
              <div
                key={i}
                className="flex-1 h-[5px] rounded-[3px]"
                style={PIP_STYLE[p.state] || PIP_STYLE.reachable}
              />
            ))}
          </div>
          <div className="text-[10px] tracking-[0.16em] text-bone-dim mt-2">
            {active.rx ? 'RX — SEE A CLINICIAN' : active.next_label}
            {active.timeline ? ` · ${active.timeline}` : ''}
          </div>
        </div>
      )}

      {/* shop + adherence pinned to the card bottom so they align across a row */}
      <div className="mt-auto pt-4">
        <AurumButton
          fullWidth
          onClick={openShop}
          trailingIcon={<LuArrowRight size={15} strokeWidth={1.75} />}
          ariaLabel={`Shop ${active.product}`}
        >
          SHOP NOW · {active.price}
        </AurumButton>

        {/* Adherence READOUT — read-only.
            The setter moved to the scan flow (components/create/ProductUsageStep):
            usage is now reported once, immediately before a reading, where it
            can actually feed that reading's score. Asking on this page meant
            answers drifted from any particular scan, and the same product got
            re-answered every visit. This shows the last thing you said.

            No `efficacyOn` prop any more: tracking is always on and there is no
            longer a control to turn it off (see Routine.js). The readout shows
            whenever there is something to show. */}
        {active.adherence && (
          <div className="mt-4 pt-[15px] border-t border-hairline-soft">
            <div className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: ADHERENCE_META[active.adherence]?.dot }}
                aria-hidden="true"
              />
              <span className="text-[11px] tracking-[0.16em] text-bone-dim">
                {ADHERENCE_META[active.adherence]?.label}
              </span>
            </div>
            <div className="font-display italic text-bone-dim text-xs mt-1.5">
              {ADHERENCE_META[active.adherence]?.verdict}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

const RoutineCard = ({ active, onAdherence, busy = false }) => {
  if (!active) return null;
  if (active.route && active.route !== ROUTINE_ROUTES.PRODUCT) {
    return <AdvisoryCard active={active} />;
  }
  return <ProductCard active={active} onAdherence={onAdherence} busy={busy} />;
};

export default RoutineCard;
